import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Icon from "../AppIcon";
import Button from "./Button";
import Input from "./Input";
import SideNavigation, { baseNavigation } from "./SideNavigation";
import { useAuth } from "context/AuthContext";
import { adminNavigationItems } from "./sidenavigation/admin/AdminSideNavigation";

const Header = () => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    role,
    sellerOrganizationType,
    logout,
    user: authUser,
    userDetails,
    profilePhotoUrl,
  } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const toggleButtonRef = useRef(null);

  const shouldReduceMotion = useReducedMotion();
  const animationDuration = shouldReduceMotion ? 0 : 0.3;
  const iconDuration = shouldReduceMotion ? 0 : 0.2;

  const isLoginPage = location.pathname === "/login";
  const isRegisterPage = location.pathname === "/registration";

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Focus trap and Escape key for mobile menu
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        toggleButtonRef.current?.focus();
        return;
      }

      if (e.key !== "Tab") return;

      const menuEl = mobileMenuRef.current;
      if (!menuEl) return;

      const focusables = [
        toggleButtonRef.current,
        ...Array.from(
          menuEl.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ),
      ].filter(Boolean);

      if (focusables.length === 0) return;

      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMobileMenuOpen]);

  // Escape key for user menu
  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isUserMenuOpen]);

  // Close user menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleNotificationClick = (notification) => {
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    setIsNotificationOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActiveRoute = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(path);
  };

  const navigationItems = useMemo(() => {
    const items = [...baseNavigation];
    const canShowSellerBusinessActivity =
      role === "Seller" &&
      sellerOrganizationType !== "Supplier" &&
      sellerOrganizationType !== "Other";

    if (role === "Security") {
      return [
        {
          label: "Events",
          path: "/events",
          icon: "Calendar",
          tooltip: "View assigned events",
        },
      ];
    }

    if (role === "Buyer") {
      items.push({
        label: "Outbound Groups & Events",
        path: "/business-activity",
        icon: "Briefcase",
        tooltip: "View outbound groups & events",
      });
    }
    if (canShowSellerBusinessActivity) {
      items.push({
        label: "Inbound Groups & Events",
        path: "/business-activity",
        icon: "Briefcase",
        tooltip: "View inbound groups & events",
      });
    }

    return items;
  }, [role]);

  if (isLoginPage) {
    return (
      <header className="bg-surface border-b border-border py-3 px-6">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center justify-start">
            <img
              src="/assets/logo/leadsLogo.png"
              alt="Leads Logo"
              className="h-10 sm:h-12 max-w-[220px] sm:max-w-[260px] object-contain"
            />
          </div>
          <div className="w-[180px]"></div>
        </div>
      </header>
    );
  }

  if (isRegisterPage) {
    return (
      <header className="bg-surface border-b border-border py-3 px-6">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center justify-start">
            <img
              src="/assets/logo/leadsLogo.png"
              alt="Leads Logo"
              className="h-10 sm:h-12 max-w-[220px] sm:max-w-[260px] object-contain"
            />
          </div>
          <button
            onClick={() => navigate("/login")}
            className="text-text-secondary hover:text-text-primary text-sm font-medium"
          >
            Already have an account?
          </button>
        </div>
      </header>
    );
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-100 bg-surface border-b border-border">
      <div className="flex items-center justify-between h-[60px] px-4 sm:px-6">
        {/* Logo */}
        <div className="flex items-center">
          {role ? (
            <button
              onClick={() =>
                role === "Admin" ? navigate("/dashboard") : navigate("/events")
              }
              className="flex items-center space-x-3 focus-ring rounded-md p-1 -ml-1"
            >
              <div className="flex items-center justify-start pl-2 sm:pl-4">
                <img
                  src="/assets/logo/leadsLogo.png"
                  alt="Leads Logo"
                  className="h-12 sm:h-14 max-w-[220px] sm:max-w-[260px] object-contain"
                />
              </div>
            </button>
          ) : (
            <div className="flex items-center justify-start pl-2 sm:pl-4">
              <img
                src="/assets/logo/leadsLogo.png"
                alt="Leads Logo"
                className="h-12 sm:h-14 max-w-[220px] sm:max-w-[260px] object-contain"
              />
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-1 sm:gap-2">
          {role ? (
            <>
              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <Button
                  variant="transparent"
                  onClick={() => {
                    const opening = !isUserMenuOpen;
                    setIsUserMenuOpen(opening);
                    if (opening) setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center px-2"
                  aria-expanded={isUserMenuOpen}
                  aria-label={
                    isUserMenuOpen ? "Close user menu" : "Open user menu"
                  }
                >
                  <div className="flex items-center bg-gray-100 px-2 sm:px-4 py-4 gap-2 sm:gap-3">
                    <div
                      className={`relative w-8 h-8 rounded-full overflow-hidden flex items-center justify-center font-medium text-sm ${
                        profilePhotoUrl
                          ? "bg-background"
                          : "bg-primary text-primary-foreground"
                      }`}
                    >
                      {profilePhotoUrl ? (
                        <img
                          src={profilePhotoUrl}
                          alt={`${userDetails?.firstName} ${userDetails?.lastName}`}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          draggable="false"
                        />
                      ) : (
                        <span className="text-sm font-semibold">
                          {userDetails?.firstName?.charAt(0)?.toUpperCase()}
                          {userDetails?.lastName?.charAt(0)?.toUpperCase()}
                        </span>
                      )}
                    </div>

                    <p className="text-primary text-md font-medium leading-none m-0">
                      {userDetails?.firstName} {userDetails?.lastName}
                      <span className="block text-text-secondary text-sm font-normal text-left">
                        {role}
                      </span>
                    </p>
                  </div>
                </Button>

                <AnimatePresence>
                  {isUserMenuOpen && (
                    <>
                      {/* User menu backdrop */}
                      <motion.div
                        key="user-menu-backdrop"
                        className="fixed inset-0 z-30"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: animationDuration * 0.7 }}
                        onClick={() => setIsUserMenuOpen(false)}
                        aria-hidden="true"
                      />
                      {/* User menu dropdown */}
                      <motion.div
                        key="user-menu-panel"
                        className="absolute right-0 top-full mt-3 bg-popover border border-border rounded-lg shadow-modal z-40 min-w-[220px]"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{
                          duration: iconDuration,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                        style={{ transformOrigin: "top right" }}
                        aria-label="User menu"
                      >
                        <div className="px-4 py-3 border-b border-border">
                          <p className="font-medium text-primary text-md">
                            {userDetails?.firstName} {userDetails?.lastName}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {userDetails?.email}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {userDetails?.designation}
                          </p>
                          <p className="text-xs text-secondary mt-1">
                            {userDetails?.organizationName}
                          </p>
                        </div>
                        <div className="py-2">
                          {role !== "Security" && (
                            <button
                              onClick={() => {
                                navigate(`/profile/${userDetails.userId}`);
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                            >
                              <Icon name="User" size={16} />
                              <span>Profile Setting</span>
                            </button>
                          )}
                          <hr className="my-2 border-border" />
                          <button
                            onClick={handleLogout}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-muted transition-colors flex items-center space-x-2 text-error"
                          >
                            <Icon name="LogOut" size={16} />
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : null}

          {/* Mobile Menu Backdrop + Panel */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <>
                {/* Backdrop overlay */}
                <motion.div
                  key="mobile-backdrop"
                  className="fixed inset-0 top-[60px] bg-black/40 z-40 lg:hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: animationDuration }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-hidden="true"
                />
                {/* Slide-down menu panel */}
                <motion.nav
                  key="mobile-menu"
                  ref={mobileMenuRef}
                  className="fixed left-0 right-0 top-[60px] z-50 bg-surface border-b border-border shadow-lg lg:hidden overflow-hidden"
                  initial={{ opacity: 0, y: "-100%" }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: "-100%" }}
                  transition={{
                    duration: animationDuration,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                  aria-label="Mobile navigation menu"
                >
                  <div className="py-2">
                    {(role === "Admin"
                      ? adminNavigationItems
                      : navigationItems
                    )?.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className={`w-full flex items-center space-x-3 py-3.5 px-5 text-left text-sm font-medium transition-colors ${
                          isActiveRoute(item.path)
                            ? "border-l-[3px] border-primary bg-primary/5 text-primary"
                            : "border-l-[3px] border-transparent text-text-primary hover:bg-muted"
                        }`}
                      >
                        <Icon name={item.icon} size={18} />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.nav>
              </>
            )}
          </AnimatePresence>

          {/* Hamburger Toggle */}
          <Button
            ref={toggleButtonRef}
            variant="ghost"
            size="icon"
            className="lg:hidden relative z-50"
            onClick={() => {
              const opening = !isMobileMenuOpen;
              setIsMobileMenuOpen(opening);
              if (opening) setIsUserMenuOpen(false);
            }}
            aria-expanded={isMobileMenuOpen}
            aria-label={
              isMobileMenuOpen
                ? "Close navigation menu"
                : "Open navigation menu"
            }
          >
            <AnimatePresence mode="wait" initial={false}>
              {isMobileMenuOpen ? (
                <motion.div
                  key="close-icon"
                  initial={{ opacity: 0, rotate: shouldReduceMotion ? 0 : -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: shouldReduceMotion ? 0 : 90 }}
                  transition={{ duration: iconDuration }}
                >
                  <Icon name="X" size={20} />
                </motion.div>
              ) : (
                <motion.div
                  key="menu-icon"
                  initial={{ opacity: 0, rotate: shouldReduceMotion ? 0 : 90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: shouldReduceMotion ? 0 : -90 }}
                  transition={{ duration: iconDuration }}
                >
                  <Icon name="Menu" size={20} />
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;
